/**
 * React Query 配置
 * 提供全局的数据获取、缓存、同步和状态管理
 */

import { QueryClient } from '@tanstack/react-query'

// 创建QueryClient实例
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 数据被认为是新鲜的时间（毫秒）
      staleTime: 1000 * 60 * 5, // 5分钟
      
      // 缓存时间
      cacheTime: 1000 * 60 * 30, // 30分钟
      
      // 失败后重试次数
      retry: 2,
      
      // 重试延迟
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // 窗口聚焦时重新获取
      refetchOnWindowFocus: false,
      
      // 网络重连时重新获取
      refetchOnReconnect: true,
      
      // 组件挂载时是否重新获取
      refetchOnMount: true,
      
      // 错误处理
      onError: (error) => {
        console.error('Query Error:', error)
      },
    },
    mutations: {
      // mutation失败后重试次数
      retry: 1,
      
      // 错误处理
      onError: (error) => {
        console.error('Mutation Error:', error)
      },
    },
  },
})

// Query Keys 工厂函数
// 统一管理所有的query keys，方便缓存管理和失效
export const queryKeys = {
  // 用户相关
  user: {
    all: ['user'],
    info: () => [...queryKeys.user.all, 'info'],
    follows: () => [...queryKeys.user.all, 'follows'],
    fans: () => [...queryKeys.user.all, 'fans'],
    center: (id) => [...queryKeys.user.all, 'center', id],
  },
  
  // 模型相关
  models: {
    all: ['models'],
    lists: () => [...queryKeys.models.all, 'list'],
    list: (filters) => [...queryKeys.models.lists(), filters],
    detail: (id) => [...queryKeys.models.all, 'detail', id],
    home: (params) => [...queryKeys.models.all, 'home', params],
    category: (category, params) => [...queryKeys.models.all, 'category', category, params],
    tag: (tag, params) => [...queryKeys.models.all, 'tag', tag, params],
    search: (keyword, params) => [...queryKeys.models.all, 'search', keyword, params],
    related: (id, params) => [...queryKeys.models.all, 'related', id, params],
    user: (userId, params) => [...queryKeys.models.all, 'user', userId, params],
  },
  
  // 材质相关
  materials: {
    all: ['materials'],
    lists: () => [...queryKeys.materials.all, 'list'],
    list: (params) => [...queryKeys.materials.lists(), params],
    my: (params) => [...queryKeys.materials.all, 'my', params],
  },
  
  // 收藏相关
  collections: {
    all: ['collections'],
    list: (params) => [...queryKeys.collections.all, 'list', params],
    dirs: () => [...queryKeys.collections.all, 'dirs'],
  },
  
  // 评论相关
  comments: {
    all: ['comments'],
    tree: (modelId) => [...queryKeys.comments.all, 'tree', modelId],
  },
  
  // 通知相关
  notifications: {
    all: ['notifications'],
    list: () => [...queryKeys.notifications.all, 'list'],
  },
  
  // 分类和标签
  taxonomy: {
    all: ['taxonomy'],
    categories: () => [...queryKeys.taxonomy.all, 'categories'],
    tags: (params) => [...queryKeys.taxonomy.all, 'tags', params],
  },
  
  // 搜索
  search: {
    all: ['search'],
    hot: () => [...queryKeys.search.all, 'hot'],
  },
}

// 辅助函数：使缓存失效
export const invalidateQueries = {
  // 使所有用户相关的查询失效
  user: () => queryClient.invalidateQueries(queryKeys.user.all),
  
  // 使所有模型列表查询失效
  modelLists: () => queryClient.invalidateQueries(queryKeys.models.lists()),
  
  // 使特定模型详情失效
  modelDetail: (id) => queryClient.invalidateQueries(queryKeys.models.detail(id)),
  
  // 使所有收藏相关查询失效
  collections: () => queryClient.invalidateQueries(queryKeys.collections.all),
}

// 辅助函数：预取数据
export const prefetchQueries = {
  // 预取模型详情
  modelDetail: async (id) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.models.detail(id),
      queryFn: () => import('@/api').then(api => api.getModelDetail(id)),
    })
  },
}

